<template>
  <v-dialog
    :model-value="true"
    :retain-focus="false"
    width="90vw"
    max-width="1200"
    content-class="ra-view-dialog-overlay"
    persistent
  >
    <section
      class="ra-dialog"
      role="dialog"
      aria-modal="true"
      aria-label="リスクアセスメント閲覧"
    >
      <header class="ra-dialog__header">
        <h2 class="ra-dialog__title">
          リスクアセスメント閲覧
        </h2>
        <div
          v-if="version"
          class="ra-dialog__version"
          data-testid="risk-assessment-version"
        >
          <span class="ra-dialog__version-label">入力ver:</span>
          <span class="ra-dialog__version-name">{{ version.shortLabel }}</span>
          <span class="ra-dialog__version-range">（{{ version.label }}）</span>
        </div>
      </header>

      <div
        v-if="loading"
        class="ra-dialog__empty"
      >
        読み込み中です…
      </div>
      <v-alert
        v-else-if="errorMessage"
        class="ra-dialog__error"
        type="error"
        variant="tonal"
      >
        {{ errorMessage }}
      </v-alert>
      <template v-else-if="version && assessment">
        <div class="ra-dialog__body">
          <div class="ra-dialog__questions">
            <RiskAssessmentQuestionTable
              :version="version"
              :draft-map="draftMap"
              :previous-answer-map="{}"
              disabled
              @select-answer-type="() => {}"
              @toggle-detail="() => {}"
            />
          </div>
        </div>

        <RiskAssessmentScoreSummary :summary="summary" />
      </template>

      <footer class="ra-dialog__actions">
        <v-btn
          variant="flat"
          color="#9e9e9e"
          min-width="112"
          @click="emit('close')"
        >
          閉じる
        </v-btn>
      </footer>
    </section>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { riskAssessmentsApi, summarizeAnswers } from "@/vue/entities/risk-assessment";
import type { PreviousAssessment, RiskAssessmentScoreSummary as RiskAssessmentScoreSummaryType, RiskAssessmentVersion } from "@/vue/entities/risk-assessment";
import type { AnswerDraft } from "../model/types";
import RiskAssessmentQuestionTable from "./RiskAssessmentQuestionTable.vue";
import RiskAssessmentScoreSummary from "./RiskAssessmentScoreSummary.vue";

const props = defineProps<{
  assessmentId: string;
}>();

const emit = defineEmits<{
  close: [];
}>();

const loading = ref(true);
const errorMessage = ref("");
const version = ref<RiskAssessmentVersion | null>(null);
const assessment = ref<PreviousAssessment | null>(null);

const draftMap = computed<Record<number, AnswerDraft>>(() => {
  if (!assessment.value) return {};

  return Object.fromEntries(assessment.value.answers.map((answer) => [answer.questionNo, answer]));
});

const summary = computed<RiskAssessmentScoreSummaryType>(() => {
  if (!version.value || !assessment.value) {
    return { applicableCount: 0, notApplicableCount: 0, unknownCount: 0, unansweredCount: 0, answeredAll: false, positive: false };
  }

  return summarizeAnswers(assessment.value.answers, {
    questionCount: version.value.questionCount,
    positiveThreshold: version.value.positiveThreshold
  });
});

onMounted(async () => {
  try {
    const response = await riskAssessmentsApi.fetchView(props.assessmentId);
    version.value = response.versions.find((v) => v.versionType === response.assessment.versionType) ?? null;
    assessment.value = response.assessment;
  } catch (e) {
    errorMessage.value = "リスクアセスメントの取得に失敗しました。";
  } finally {
    loading.value = false;
  }
});
</script>

<style lang="scss">
// v-dialog の中身は body 直下へ teleport されるため scoped が効かない。
.v-overlay__content.ra-view-dialog-overlay {
  margin: 20px;
  max-height: calc(100% - 40px);
}
</style>

<style lang="scss" scoped>
.ra-dialog {
  background-color: #fff;
  border-radius: 4px;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 16px 24px 24px;
}

.ra-dialog__header {
  background-color: #e0f6f6;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 16px;
}

.ra-dialog__title {
  color: #33B5B4;
  font-size: 18px;
  font-weight: bold;
  text-align: center;
}

.ra-dialog__version {
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  justify-content: center;
  margin-top: 6px;
  text-align: center;
}

.ra-dialog__version-label {
  color: rgba(0, 0, 0, 0.6);
  font-size: 12px;
}

.ra-dialog__version-name {
  color: #277f7e;
  font-size: 15px;
  font-weight: bold;
}

.ra-dialog__version-range {
  color: rgba(0, 0, 0, 0.6);
  font-size: 12px;
}

.ra-dialog__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.ra-dialog__error {
  margin-top: 12px;
}

.ra-dialog__questions {
  margin-top: 8px;
}

.ra-dialog__empty {
  text-align: center;
  color: rgba(0, 0, 0, 0.6);
  padding: 32px 0;
}

.ra-dialog__actions {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 16px;
}
</style>
